import React, { Component } from "react";
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import UserService from '../../../Services/UserService';
import { Col, Row, Form } from "react-bootstrap";
import debounce from 'es6-promise-debounce';
import userPlaceholderImg from "images/user_image_placeholder.svg";

class UserListEditor extends Component {
  static propTypes = {
    value: PropTypes.array.isRequired,
    memberRequest: PropTypes.array,
    availableRoles: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    onPreviewEditEnd: PropTypes.func,
    disable: PropTypes.bool,
    preview: PropTypes.bool,
    protectAdmin: PropTypes.bool,
  };

  static defaultProps = {
    memberRequest: [],
    disable: false,
    preview: false,
    protectAdmin: false,
  };

  constructor(props) {
    super(props);

    this.ref = React.createRef();

    const users = UserListEditor.getPreviewUserMergeList(this.props.availableRoles, this.props.value, this.props.memberRequest);

    this.state = {
      errorMessage: null,
      preview: props.preview,
      value: users,
      isAdminRoleUse: typeof(users.find(u => props.availableRoles[u.roleId].value === 'ADMIN')) != 'undefined',
    };
  }

  componentDidMount() {
    if (this.ref && !this.ref.enablePreviewEdit) {
      this.ref.enablePreviewEdit = this.enablePreviewEdit;
      this.ref.disablePreviewEdit = this.disablePreviewEdit;
      this.ref.validateSelection = this.validateSelection;
      this.validateSelection();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.value != prevProps.value || this.props.memberRequest != prevProps.memberRequest) {
      const users = UserListEditor.getPreviewUserMergeList(this.props.availableRoles, this.props.value, this.props.memberRequest);
      this.setState({
        value: users,
        memberRequest: this.props.memberRequest,
        isAdminRoleUse: typeof(users.find(u => this.props.availableRoles[u.roleId].value === 'ADMIN')) != 'undefined',
      });
    }
  }

  static getPreviewUserMergeList = (availableRoles, users, memberRequest) => {
    const formatUserObject = (u, i) => ({
        displayName: u.user.firstName + ' ' + u.user.lastName,
        firstName: u.user.firstName,
        lastName:  u.user.lastName,
        provisioningStatus: null,
        provisioningStatusText: null,
        roleId: UserListEditor.getUserRoleId(availableRoles, u.profileTypeRefId),
        photo: u.user.photoUrl,
        upn: u.user.upn,
        key: i,
        value: u.user.mail,
      });

    let currentUsers = users.map(formatUserObject);

    if (memberRequest.length) {
      const newUsers = memberRequest[memberRequest.length - 1].projectMemberRequestUsers.map(formatUserObject);

      currentUsers = currentUsers.map(currentUser => {
        const searchNewUser = newUsers.find(newUser => newUser.value === currentUser.value);

        if (!searchNewUser) { // user will be remove
          currentUser.provisioningStatus = (<i className='icon-circle-minus' />);
          currentUser.provisioningStatusText = 'Removal request pending';
        } else if (searchNewUser.roleId != currentUser.roleId) {  // user role will change
          currentUser.provisioningStatus = (<i className={searchNewUser.roleId < currentUser.roleId ? 'icon-cloud-download' : 'icon-cloud-upload'} />);
          currentUser.provisioningStatusText = 'Role update pending (' + availableRoles[currentUser.roleId].displayName + ' 🡆 ' + availableRoles[searchNewUser.roleId].displayName + ')';
        }

        return currentUser;
      });

      // adding new user
      currentUsers = currentUsers.concat(newUsers
        .filter(newUser => !currentUsers.find(currentUser => currentUser.value === newUser.value))
        .map(addedUser => {
          addedUser.provisioningStatus = (<i className='icon-circle-plus' />);
          addedUser.provisioningStatusText = 'Add request pending';
          return addedUser;
        })
      );

    }

    return UserListEditor.sortUsers(currentUsers);
  }

  getUserApiValue(users) {
    return users
      .map(u => (
        {
          "profileTypeRefId": this.props.availableRoles[u.roleId].value,
          "user": {
            "upn": u.upn,
            "firstName": u.firstName,
            "lastName": u.lastName,
            "mail": u.value,
            "photoUrl": u.photo,
          }
        }
      ));
  }

  static getUserRoleId(availableRoles, roleId) {
    for (let i = 0; i < availableRoles.length; i++) {
      if (availableRoles[i].value === roleId) {
        return i;
      }
    }
  }

  static sortUsers(users) {
    return users.sort((a, b) => {
      return (a.roleId == b.roleId ? a.displayName.localeCompare(b.displayName) : (a.roleId < b.roleId ? -1 : 1));
    });
  }

  onChange = (usersChange) => {
    if (usersChange === null) {
      usersChange = [];
    }

    let addedUsers = usersChange
      .filter(addUser => !this.state.value.find(user => user.value == addUser.value))
      .map(u => {
        let newUser = { ...u };
        delete newUser.isSearchMenuOption;
        return newUser;
      });
    const users = UserListEditor.sortUsers([ ...this.state.value, ...addedUsers ]);

    if (this.props.onChange) {
      this.props.onChange(this.getUserApiValue(users));
    }

    this.setState({ value: users }, this.validateSelection);
  }

  updateUserRole = (user, value) => {
    user.roleId = UserListEditor.getUserRoleId(this.props.availableRoles, value);

    if (this.props.onChange) {
      this.props.onChange(this.getUserApiValue(this.state.value));
    }

    this.setState({
      value: [ ...this.state.value ],
      isAdminRoleUse: typeof(this.state.value.find(u => this.props.availableRoles[u.roleId].value === 'ADMIN')) !== 'undefined',
    }, this.validateSelection);
  }

  enablePreviewEdit = () => {
    if (this.props.preview && this.state.preview && this.props.memberRequest.length === 0) {
      this.setState({ preview: false }, () => {
        if (this.selectRef && this.selectRef.current) {
          this.selectRef.current.focus();
        }
      });
    }
  }

  disablePreviewEdit = () => {
    if (this.props.preview && !this.state.preview && this.validateSelection()) {
      this.setState({ preview: true });
      if (this.props.onPreviewEditEnd) {
        this.props.onPreviewEditEnd(this.getUserApiValue(this.state.value));
      }
    }
  }

  validateSelection() {
    let errorMessage = null;

    if (this.state.value.length === 0) {
      errorMessage = 'Select at least one member.';
    } else if (!this.state.isAdminRoleUse) {
      errorMessage = 'One member should be administrator.';
    }

    if (errorMessage != this.state.errorMessage) {
      this.setState({ errorMessage });
    }

    return !errorMessage;
  }

  removeTeamMember = (index) => {
    const users = [ ...this.state.value ];
    users.splice(index, 1);
    this.setState({
      value: users,
      isAdminRoleUse: typeof(users.find(u => this.props.availableRoles[u.roleId].value === 'ADMIN')) !== 'undefined',
    }, this.validateSelection);
  }

  requestADUser = (searchText) => {

    if (!searchText || !searchText.trim().length) {
      return new Promise(r => r([]));
    }

    searchText = searchText.trim();

    return UserService.searchUser(searchText).then((users) => {
      return users.map(user => ({
        displayName: (user.firstName != null ? user.firstName: 'Unknown') + ' ' + (user.lastName != null ? user.lastName : 'Unknown'),
        firstName: user.firstName ? user.firstName : 'Unknown',
        lastName:  user.lastName ? user.lastName : 'Unknown',
        upn: user.userPrincipalName,
        provisioningStatus: null,
        provisioningStatusText: null,
        isSearchMenuOption: true,
        key: user.objectId,
        roleId: 1,
        value: user.mail,
        photo: user.photoDataUrl,
      }));
    });
  }

  render() {
    return (
      <div
        className={
          'userListEditor' +
          (this.state.preview ? ' userListEditor-preview' : '') +
          (this.props.disable ? ' userListEditor-disable' : '')
        }
        ref={e => this.ref = e}
      >
        <div className='userList'>
          { this.state.value.map((user, i) => ((this.state.preview) ?
          (
            <Row key={i} className="userListItem" title={user.provisioningStatusText}>
              <Col className='identificationUserPicture'>
                <img alt={user.displayName} className='faceCapture rounded-circle' src={user.photo ? user.photo : userPlaceholderImg} />
                <span className='identificationUserProvisioningStatus'>{user.provisioningStatus}</span>
              </Col>
              <Col className='identificationLabel' md={0}>
                {user.displayName.trim() == '' ? user.value : user.displayName}
              </Col>
              <Col className='identificationRole' md={true}>{' (' + this.props.availableRoles[user.roleId].displayName + ')'}</Col>
            </Row>
          ) : (
            <Row key={i} className="userListItem">
              <Col className='identificationUserPicture'>
                <img alt={user.displayName} className='faceCapture rounded-circle' src={user.photo ? user.photo : userPlaceholderImg} />
              </Col>
              <Col className='identificationLabel' md={true}>
                <span className='identificationMail'>{user.value}</span><br/>
                <span className='identificationDisplayName'>{user.displayName}</span>
              </Col>
              <Col className='identificationRoleSelect'>
                <Form.Control
                  as="select"
                  className='role-select'
                  value={this.props.availableRoles[user.roleId].value}
                  onChange={(s) => this.updateUserRole(user, s.target.value)}
                  { ...((this.props.disable || !!user.provisioningStatus || (this.props.protectAdmin && this.props.availableRoles[user.roleId].value == 'ADMIN')) && { readOnly: 'readOnly', disabled: 'disabled' }) }
                >
                  { this.props.availableRoles
                    .map((role) => (
                      <option key={role.value} value={role.value}>{role.displayName}</option>
                  ))}
                </Form.Control>
              </Col>
              <Col className="memberRemove">
                { (!this.props.protectAdmin || this.props.availableRoles[user.roleId].value != 'ADMIN') && (
                    <i onClick={() => this.removeTeamMember(i)} className="icon-trash" />
                )}
              </Col>
            </Row>
          )
        ))}
        </div>
        <div className='formInputTextHelper'>
          {this.state.errorMessage}
        </div>
        {(this.props.disable || this.state.preview || this.props.memberRequest.length > 0) ? null : (
          <AsyncSelect
            focusOption
            cacheOptions
            defaultOptions
            loadOptions={debounce(this.requestADUser, 265)}
            className={"userListEditorSelect" + (!this.state.value ? ' is-invalid': ' is-valid')} /* TODO */
            classNamePrefix="react-select"
            value={this.state.value}
            isMulti
            name="users"
            isClearable={false}
            placeholder={'Search member'}
            noOptionsMessage={() => 'Member not found'}
            //menuIsOpen={true} // FOR DEBUG
            components={{
              DropdownIndicator: () => (<div className='userSelectorIndicator'><i className='icon-more-little'></i></div>),
              IndicatorSeparator: () => null,
              MultiValueContainer: (props) => {
                return !props.data.isSearchMenuOption ? null : (
                  <components.MultiValueContainer {...props} />
                );
              },
            }}
            formatOption
            formatOptionLabel={(user) => (
              !user.isSearchMenuOption ? null : (
                <Row className='userListEditionOptionLabel'>
                  <Col className='identificationUserPicture'>
                    <img alt={user.displayName} className='faceCapture rounded-circle' src={user.photo ? user.photo : userPlaceholderImg} />
                  </Col>
                  <Col className='identificationLabel' md={true}>
                    <span className='identificationMail'>{user.value}</span><br/>
                    <span className='identificationDisplayName'>{user.displayName}</span>
                  </Col>
                </Row>
              )
            )}
            onChange={(tags) => this.onChange(tags)}
            ref={this.selectRef}
          />
        )}
      </div>
    );
  }
}

export default UserListEditor;
